/**
 * Textos de UI centralizados.
 *
 * v1: solo español. Cuando llegue el inglés (v3), se duplica este archivo
 * como `en.ts` y se introduce un selector trivial.
 *
 * Convenciones de marca aplicadas (ver docs/brand/02-voice-and-tone.md):
 * - Tuteo siempre.
 * - Sin exclamaciones.
 * - Sin emojis.
 * - Sentence case en CTAs.
 * - No empezar por "Hola".
 * - "El equipo de Nexum" para cierres.
 */

export const t = {
  app: {
    name: "Nexum",
    tagline: "El vínculo entre tu vida y tu plan.",
  },
  auth: {
    // Páginas
    loginTitle: "Entra en Nexum",
    registerTitle: "Crea tu cuenta",
    resetTitle: "Restablece tu contraseña",
    resetConfirmTitle: "Crea una contraseña nueva",

    // Campos
    emailLabel: "Email",
    emailPlaceholder: "tu@email.com",
    passwordLabel: "Contraseña",
    passwordPlaceholder: "Mínimo 8 caracteres",
    newPasswordLabel: "Nueva contraseña",
    fullNameLabel: "Nombre",
    fullNamePlaceholder: "Tu nombre (opcional)",
    // Sprint 7 Bloque B1: campo de invite_code en /register (beta privada).
    inviteCodeLabel: "Código de invitación",
    inviteCodePlaceholder: "NEXUM-XXXX",

    // Sprint 8 S8.4 (UX-008): copy del flow OAuth Google → /register
    // simplificado cuando el backend devuelve oauth_pending=true.
    oauthPendingTitle: "Termina tu registro",
    oauthPendingSubtitle:
      "Conectaste con Google como {email}. Solo necesitas tu código de invitación.",
    // Sprint 14 S14.3: Apple no usa invite_code; solo falta el consentimiento.
    oauthPendingSubtitleApple:
      "Conectaste con Apple como {email}. Solo falta tu consentimiento.",
    oauthPendingSubmit: "Completar registro",
    oauthSessionExpired:
      "Tu sesión de Google expiró. Empieza de nuevo.",
    oauthRestart: "Empieza de nuevo el registro.",
    emailReserved:
      "Esta cuenta está bloqueada. Contacta soporte escribiendo a hola@nexumrun.com.",

    // CTAs
    loginCta: "Entrar",
    registerCta: "Crear cuenta",
    logoutCta: "Cerrar sesión",
    requestResetCta: "Enviar enlace",
    confirmResetCta: "Cambiar contraseña",
    googleCta: "Continuar con Google",
    googleSeparator: "o con tu email",
    appleCta: "Continuar con Apple",

    // F3 (pre-flight): en /login, "Crear cuenta" es el CTA PRIMARIO para
    // recién llegados (beta gated por invite — la mayoría llega a registrarse).
    // El login queda como flujo secundario para quien ya tiene cuenta. Solo
    // jerarquía visual; el routing/guard no cambia.
    loginNewHerePrompt: "¿Primera vez en Nexum? Crea tu cuenta para empezar.",
    loginAlreadyHaveAccount: "¿Ya tienes cuenta? Entra:",

    // Sprint 14 S14.3 (R2 GDPR): consentimiento en registro. El label se
    // monta en JSX intercalando los enlaces a /terms y /privacy.
    consentLabelPrefix: "He leído y acepto los ",
    consentTermsLink: "términos de servicio",
    consentLabelMid: " y la ",
    consentPrivacyLink: "política de privacidad",
    consentLabelSuffix: ".",

    // Links entre páginas
    toRegister: "¿Aún no tienes cuenta? Créala",
    toLogin: "¿Ya tienes cuenta? Entra",
    toReset: "¿Olvidaste tu contraseña?",
    backToLogin: "Volver al inicio de sesión",

    // Estados de éxito (texto neutro, sin exclamaciones)
    resetEmailSent:
      "Si tu email está en Nexum, te llega un enlace en breve. Revisa también la carpeta de spam.",
    passwordChanged: "Hecho. Entra con tu nueva contraseña.",

    // Aviso al inicio del plan cuando el horizonte hasta la carrera es escaso.
    // El motor adaptativo (Sprint 6) marca el plan con near_race_warning=True
    // y la home renderiza este copy literal. Decisión Alberto 2026-05-09
    // sesión 2: el sistema NUNCA rechaza al usuario, siempre genera plan
    // adaptado + muestra este aviso. Documentado en
    // docs/specs/02-onboarding.md §"Comportamiento ante carrera muy próxima"
    // y docs/sprints/sprint-06-adaptive-plan-v2.md §Regla 6.
    planNearRaceWarning:
      "Tu carrera está cerca. Hemos adaptado el plan pero no es el escenario ideal — tu prioridad será mantener forma, no maximizar rendimiento.",

    // Errores — códigos del backend traducidos a copy de marca
    errors: {
      // Validación
      email_required: "Necesito tu email.",
      email_invalid: "Ese email no parece válido.",
      email_too_long: "Email demasiado largo.",
      password_required: "Necesito tu contraseña.",
      password_too_short: "La contraseña tiene que tener al menos 8 caracteres.",
      password_too_long: "Contraseña demasiado larga.",
      full_name_too_long: "Nombre demasiado largo.",
      // Sprint 7 Bloque B1: invite_code en beta privada
      invite_code_required: "Necesitas un código de invitación para registrarte.",
      invite_code_invalid: "Código no válido. Revisa que esté bien escrito.",
      invite_code_expired: "Este código ha caducado. Solicita uno nuevo.",
      invite_code_exhausted: "Este código ya se ha usado.",
      // Sprint 14 S14.3 (R2 GDPR): consentimiento obligatorio en registro.
      terms_not_accepted:
        "Tienes que aceptar los términos de servicio y la política de privacidad para crear tu cuenta.",
      // Backend
      email_already_registered: "Ese email ya está en Nexum. Entra o restablece tu contraseña.",
      email_reserved: "Ese email pertenece a una cuenta cerrada. Usa otro o contacta con soporte.",
      invalid_credentials: "Email o contraseña incorrectos.",
      // Sprint 14 S14.5 (R3 GDPR): reauth en borrado de cuenta.
      invalid_password: "Contraseña incorrecta.",
      // Sprint 14 v12 Bloque A: edición de perfil.
      hr_max_out_of_range: "Frecuencia cardíaca fuera de rango (40-230 ppm).",
      hr_max_invalid: "Frecuencia cardíaca no válida.",
      shoe_too_long: "El nombre de la zapatilla es demasiado largo.",
      // Sprint 14 v12 Bloque B: edición con regeneración.
      plan_regeneration_failed:
        "No se pudo regenerar el plan. Revisa tus datos e inténtalo de nuevo.",
      invalid_level: "Nivel no válido.",
      invalid_surface: "Superficie no válida.",
      invalid_training_days: "Número de días no válido.",
      token_required: "El enlace no es válido.",
      token_invalid: "El enlace no es válido.",
      token_expired: "El enlace ha caducado. Solicita uno nuevo.",
      reset_token_invalid: "El enlace ha caducado o ya se usó.",
      user_invalid: "Tu cuenta no está activa. Contacta con soporte.",
      // Google Sign-In
      id_token_required: "No recibimos el token. Vuelve a intentarlo.",
      google_token_invalid: "Tu sesión de Google no es válida. Vuelve a intentarlo.",
      google_email_unverified: "Tu email de Google no está verificado. Verifícalo y vuelve.",
      oauth_not_configured: "El proveedor no está disponible ahora. Entra con tu email.",
      // Apple Sign-In
      apple_token_invalid: "Tu sesión de Apple no es válida. Vuelve a intentarlo.",
      apple_email_unverified: "Tu email de Apple no está verificado. Verifícalo y vuelve.",
      // Genéricos
      network_error: "Sin conexión con el servidor. Vuelve a intentarlo.",
      unknown_error: "Algo no ha ido bien. Vuelve a intentarlo.",
    },
  },
  home: {
    welcome: "Has iniciado sesión.",
    planSectionTitle: "Mi plan",
    planNoneCta: "No tienes plan activo. Crea uno para empezar.",
    planNoneButton: "Crear plan",
    planActiveButton: "Ver plan",
    // Sprint 13 S13.10b: estado "decisión pendiente" (recovery terminó /
    // progresión venció). El CTA lleva a /plan donde se elige el objetivo.
    planDecisionCta: "Has terminado un ciclo. Elige tu próximo objetivo.",
    planDecisionButton: "Elegir objetivo",
    // Sprint 14.0: el link va a la pantalla de SOLO LECTURA "Mis datos"
    // (antes apuntaba al onboarding en blanco, que confundía).
    editProfileLink: "Mis datos",
  },

  // s64 — Barra de navegación inferior fija (móvil): Inicio · Plan · Carreras.
  nav: {
    label: "Navegación principal",
    home: "Inicio",
    plan: "Plan",
    races: "Carreras",
  },

  // Sprint 14.0 — Pantalla "Mis datos" (perfil, SOLO LECTURA).
  profile: {
    pageEyebrow: "tu perfil",
    pageTitle: "Mis datos",
    loading: "Cargando tus datos…",
    error: "No se han podido cargar tus datos. Inténtalo de nuevo.",
    retry: "Reintentar",
    back: "← Volver",
    empty:
      "Aún no has completado tu perfil. Complétalo para generar tu plan.",
    emptyCta: "Completar perfil",
    notSpecified: "Sin especificar",
    // Labels de campos.
    fieldLevel: "Nivel",
    fieldDevice: "Reloj / dispositivo",
    fieldSurface: "Superficie",
    fieldDays: "Días de entrenamiento por semana",
    fieldHrMax: "Frecuencia cardíaca máxima",
    fieldVam: "VAM (ritmo)",
    fieldLastVamTest: "Último test VAM",
    // Valores legibles.
    levelBeginner: "Principiante",
    levelAmateur: "Amateur",
    deviceWatchHr: "Reloj con pulsómetro",
    deviceWatchNoHr: "Reloj sin pulsómetro",
    deviceMobileOnly: "Solo móvil (sin reloj)",
    surfaceAsfalto: "Asfalto",
    surfaceTrail: "Trail",
    surfaceMixto: "Mixto",
    surfacePista: "Pista",
    hrMaxUnit: "ppm",
    paceUnit: "/km",
    // Sprint 14 S14.5 — Borrado de cuenta (R3 GDPR, Art.17). Irreversible.
    deleteTitle: "Borrar mi cuenta",
    deleteIntro:
      "Borra tu cuenta y todos tus datos de forma permanente.",
    deleteButton: "Borrar mi cuenta",
    deleteModalTitle: "¿Seguro que quieres borrar tu cuenta?",
    deleteModalBody:
      "Esta acción es irreversible. Se borrarán tu perfil, tu plan, tus actividades y todos tus datos. No podremos recuperarlos.",
    deletePasswordLabel: "Confirma con tu contraseña",
    deleteConfirmWord: "BORRAR",
    deleteConfirmWordLabel: "Escribe BORRAR para confirmar",
    deleteConfirmCta: "Borrar definitivamente",
    deleteCancel: "Cancelar",
    deleteError: "No se ha podido borrar la cuenta. Inténtalo de nuevo.",
    deleteSuccess: "Tu cuenta y tus datos se han borrado.",
    // Sprint 14 v12 Bloque A — Edición de perfil (FC máx + zapatilla).
    editButton: "Editar",
    saveButton: "Guardar",
    cancelButton: "Cancelar",
    fieldShoe: "Zapatilla",
    hrMaxEditPlaceholder: "Ej. 185",
    shoeEditPlaceholder: "Ej. Nike Vaporfly 3 (opcional)",
    hrMaxLowWarning: "Valor inusualmente bajo, ¿es correcto?",
    saveError: "No se han podido guardar los cambios. Inténtalo de nuevo.",
    // Sesión 33 EXTENDIDA — Alba feature flexible scheduling (Conversación 2 Alberto).
    // Toggle UX-only en perfil: cambia render de SessionCard (día vs "Sesión N").
    // NO regenera plan, NO toca backend session_date (matching Strava intacto).
    flexibleSchedulingLabel: "Agenda flexible",
    flexibleSchedulingHelp:
      "Muestra tus sesiones como \"Sesión 1, 2, 3\" en lugar de días fijos. Útil si tu semana no sigue un calendario fijo.",
    // Sprint 14 v12 Bloque B2 — Edición CON regeneración (nivel/superficie/días).
    regenSectionTitle: "Entrenamiento",
    regenSectionNote: "Cambiar estos datos regenerará tu plan.",
    regenBadge: "Regenera el plan",
    fieldLevelEdit: "Nivel",
    fieldSurfaceEdit: "Superficie",
    fieldDaysEdit: "Días por semana",
    // v16.3 §3: dispositivo/reloj editable con regeneración del plan.
    fieldDeviceEdit: "Reloj / pulsómetro",
    regenModalTitle: "¿Regenerar tu plan?",
    regenModalBody:
      "Si cambias este dato, tu plan actual se regenerará y perderás el progreso acumulado en las sesiones futuras. Las sesiones ya completadas se conservan en tu historial. ¿Quieres continuar?",
    regenConfirm: "Continuar",
    regenModalCancel: "Cancelar",
    savePartialError:
      "Guardamos tu FC máxima y zapatilla, pero no se pudo regenerar el plan. Inténtalo de nuevo.",
  },

  // Sprint 14 S14.2 — Páginas legales (R1 GDPR). El contenido íntegro de los
  // documentos vive en src/content/legal.ts (verbatim, aprobado por PO); aquí
  // solo strings de UI (navegación / acceso).
  legal: {
    back: "← Volver",
    footerPrivacy: "Privacidad",
    footerTerms: "Términos",
  },
  onboarding: {
    // Sprint 9 S9.4b: wizard refactor a v7.1.
    // Orden steps nuevo: nivel → días → superficie → reloj+FC.

    // Páginas
    title: "Vamos a conocerte",
    progressTemplate: "{n} de {total}",
    welcomeToast: "Listo. Bienvenido a Nexum.",

    // Botones
    next: "Siguiente",
    back: "Atrás",
    finish: "Continuar",

    // Step 1 — Nivel (2 niveles v7.1)
    step1Title: "Vamos a conocerte",
    step1Question: "¿Cómo te describirías como corredor?",
    step1Help: "Sé honesto. Podrás ajustar más adelante.",
    step1OptionBeginner: "Principiante",
    step1OptionBeginnerDesc: "Nunca he salido a correr como deporte.",
    step1OptionAmateur: "Amateur",
    step1OptionAmateurDesc:
      "Tengo alguna experiencia previa (cualquier nivel).",

    // Step 2 — Días disponibles (1-7 con cap a 4)
    step2Question: "¿Cuántos días a la semana puedes entrenar?",
    step2Subtitle:
      "Recomendamos máximo 4 días para evitar sobreentrenamiento.",
    step2CapWarning:
      "Ajustaremos tu plan a 4 días para evitar sobreentrenamiento.",
    step2DaysSingular: "{count} día",
    step2DaysPlural: "{count} días",

    // Step 3 — Superficie (vocab v7.1: ambos)
    step3Question: "¿Dónde sueles correr?",
    step3OptionAsfalto: "Asfalto",
    step3OptionAsfaltoDesc: "Calles, pista de atletismo, paseos urbanos.",
    step3OptionTrail: "Trail",
    step3OptionTrailDesc: "Montaña, sendero, terreno irregular.",
    step3OptionAmbos: "Ambos",
    step3OptionAmbosDesc: "Combino los dos.",

    // Step 4 — Reloj + FC máxima opcional
    step4Question: "¿Usas reloj o pulsera deportiva cuando corres?",
    step4OptionYes: "Sí",
    step4OptionYesDesc: "Garmin, Apple Watch, Polar, etc.",
    step4OptionNo: "No",
    step4OptionNoDesc: "Solo móvil o sin tracking.",
    step4FcMaxLabel: "Frecuencia cardíaca máxima (opcional)",
    step4FcMaxHelper:
      "Si conoces tu FC máxima por un test previo. Si no, la calcularemos en tu primera sesión.",
    step4FcMaxPlaceholder: "Ej: 185",
    step4NoteSensationMode:
      "Sin problema. Trabajaremos por sensaciones (ritmos suaves, conversación, esfuerzo).",

    // Toasts
    capToast:
      "Hemos ajustado tu plan a 4 días/semana para evitar sobreentrenamiento.",

    // Errores cliente
    errorRequired: "Selecciona una opción para continuar.",
    errorDaysOutOfRange: "Selecciona entre 1 y 7 días.",
    errorFcMaxRange: "La FC máxima debe estar entre 120 y 220.",
    errorFcMaxRequired: "Introduce un valor numérico.",

    // Errores backend (códigos del endpoint v7.1)
    errorLevelInvalid: "Nivel no válido.",
    errorDaysAvailableInvalid: "Días no válidos. Selecciona entre 1 y 7.",
    errorSurfaceInvalid: "Superficie no válida.",
    errorHasWatchInvalid: "Selecciona si usas reloj.",
    errorFcMaxOutOfRange: "La FC máxima debe estar entre 120 y 220.",
    errorGeneric: "Algo salió mal, inténtalo de nuevo.",
  },
  plan: {
    // Páginas
    pageTitle: "Mi plan",
    detailTitle: "Sesión",
    createTitle: "Crear plan",
    notFoundTitle: "Sin plan activo",
    notFoundCta: "Aún no tienes un plan. Empieza creando uno.",
    notFoundButton: "Crear plan",

    // Phases (brief Q1.2)
    phaseAcumulacion: "Acumulación",
    phaseIntensificacion: "Intensificación",
    phasePeak: "Peak",
    phaseTaper: "Taper",
    // v16.3 §G.2: label de fallback. Incluido por exhaustividad — la
    // race_week se renderiza en RaceWeekSection (no en WeekNavigator),
    // así que esta clave NO se muestra al atleta en la práctica.
    phaseRaceWeek: "Pre-carrera",
    weekIndicator: "Semana {n} de {total} · {phase}",

    // Navigator
    prevWeek: "Anterior",
    nextWeek: "Siguiente",
    thisWeek: "Esta semana",
    // s63 (S-3 #5): el botón de salto tenía el MISMO texto que el badge
    // "ESTA SEMANA" y confundía en semanas no-actuales.
    goToThisWeek: "Ir a la semana actual",

    // Session card
    dayShortMon: "Lun",
    dayShortTue: "Mar",
    dayShortWed: "Mié",
    dayShortThu: "Jue",
    dayShortFri: "Vie",
    dayShortSat: "Sáb",
    dayShortSun: "Dom",
    sessionTypeRest: "Descanso",
    sessionTypeZ2: "Rodaje Z2",
    sessionTypeZ2Fartlek: "Z2 + fartlek",
    // Sprint 12 S12.4 (motor v9.2 §2): unificación visual "CaCo". El atleta
    // ve "CaCo" sin sub-tipo. (v13 §1: caco_recovery eliminado del motor.)
    sessionTypeCacoZ2: "CaCo",
    sessionTypeCacoPlus: "CACO+",
    sessionTypeSeriesCortas: "Series cortas",
    sessionTypeSeriesMedias: "Series medias",
    sessionTypeTempoContinuo: "Tempo continuo",
    sessionTypeLargaProgresiva: "Larga progresiva",
    // v15.3: rodaje continuo moderado en Z3 (por sensaciones).
    sessionTypeRodajeZ3: "Rodaje Z3",
    // v15.2b.1: tempo largo (bloques Z2-Z3/Z4, día 4 del 4d-maratón).
    sessionTypeTempoLargo: "Tempo largo",
    // v15.2-taper: sesión combinada Z2 + tempo Z3-Z4 (1d/sem en taper).
    sessionTypeTaperCombo: "Combinada (taper)",
    // v15.4-tipo: "Series de cuesta" (cuestas_v15) — sesión NUEVA y
    // SEPARADA de "CACO+" (caco_plus, la "Cuestas clásica"). Trail
    // amateur exclusivamente. Label distinto para que el atleta
    // distinga claramente las dos sesiones de cuestas en el plan.
    sessionTypeCuestasV15: "Series de cuesta",
    sessionTypeTestVam: "TEST-VAM",
    // F3 (sesión 51): exportación FIT a reloj.
    fitExportButton: "Exportar a reloj",
    // s53.2 (E.1): botón Marcar en la lista + mini-modal de esfuerzo.
    markSessionButton: "Marcar hecha",
    markSessionTitle: "¿Cómo ha ido?",
    markSessionSaving: "Guardando…",
    markSessionError: "No se pudo marcar. Inténtalo de nuevo.",
    fitExportDownloading: "Descargando…",
    fitExportNeedsVam:
      "Completa el Test VAM para exportar entrenamientos a tu reloj",
    fitExportError: "No se pudo exportar. Inténtalo de nuevo.",
    // s57 fix: la ruta de importación web de Garmin publicada en s55 NO
    // existe (verificado con cuenta real + docs oficiales). Aviso
    // provisional hasta que Alberto valide el método USB (T-smoke v3).
    fitHelpToggle: "¿Cómo pasarlo al reloj?",
    fitHelpNotice:
      "Importante: Garmin no permite importar archivos de entrenamiento "
      + "desde su web ni desde su app. Estamos verificando el método "
      + "alternativo (copiar el archivo al reloj por cable USB) y "
      + "publicaremos aquí los pasos exactos muy pronto. El archivo que "
      + "descargas es válido y podrás usarlo entonces.",
    // v16.6 §2.3: mensaje motivador en la card del TEST-VAM.
    testVamEncouragement:
      "Dalo todo en este test: no es obligatorio, pero cuanto más preciso, "
      + "mejor se ajustará tu plan a tu ritmo real.",
    // v13 §1: caco_recovery eliminado. rod_z2_corto se conserva (sustituta
    // por reporte de fatiga, mismo día).
    sessionTypeRodZ2Corto: "Rodaje Z2 corto",
    // S10.3: rodaje largo continuo (Z2) sobre terreno trail.
    sessionTypeLongRunTrail: "Rodaje largo (trail)",
    // Pre-flight F16: rodaje suave por sensaciones previo al Test VAM.
    sessionTypeRodajePrevio: "Rodaje suave",
    // v16.3 C.1 (Q1 Alberto): activación pre-carrera del día previo a race.
    sessionTypeActivacionPreCarrera: "Activación pre-carrera",

    // Estados (brief lifecycle)
    statusPlanned: "Pendiente",
    statusCompleted: "Completada",
    statusPartial: "Parcial",
    statusSkipped: "Saltada",
    // Sprint 8 S8.2 (UX-006): estado derivado planned + fecha pasada.
    // Visible solo en UI (no persistido en BD).
    statusOverdue: "Vencida",

    // Detail
    structureWarmup: "Calentamiento",
    structureMain: "Parte principal",
    structureCooldown: "Vuelta a la calma",
    actualActivityTitle: "Tu actividad registrada",
    targetDistance: "Distancia objetivo",
    targetDuration: "Duración objetivo",
    intensity: "Intensidad",

    // Acciones
    completeButton: "Marcar completada",
    // Sprint 12 S12.7 (v9.2 §1): "Saltar" ahora solo cubre el caso
    // físico (dolor/lesión). El caso logístico se resuelve con
    // "Posponer" o (futuro) "Mover".
    skipButton: "Saltar por dolor/lesión",
    deletePlanButton: "Eliminar plan",
    notesPlaceholder: "Notas (opcional)",

    // v14_2 §8 — modal post-sesión (rama SIN Strava). 5 campos opcionales.
    postSessionModalTitle: "¿Cómo fue el entrenamiento?",
    postSessionModalIntro:
      "Todo es opcional — puedes guardar sin rellenar nada.",
    postSessionHrAvgLabel: "FC media (ppm)",
    postSessionHrMaxLabel: "FC máxima (ppm)",
    postSessionPaceLabel: "Ritmo medio /km",
    postSessionPacePlaceholder: "mm:ss (ej. 5:30)",
    postSessionEffortLabel: "Esfuerzo percibido (1-5)",
    postSessionFeelingLabel: "Sensaciones",
    postSessionSaveButton: "Guardar entrenamiento",
    postSessionCloseButton: "Cerrar",
    postSessionCloseNudge:
      "Estos datos ayudan a mejorar tus entrenamientos futuros.",
    postSessionInvalid: "Revisa los datos: FC 40-230, ritmo mm:ss, esfuerzo 1-5.",
    feelingMuyBien: "Muy bien",
    feelingBien: "Bien",
    feelingRegular: "Regular",
    feelingMal: "Mal",
    // Render de resultado de una sesión completada (datos manuales).
    postSessionResultTitle: "Tu resultado",
    resultHrAvg: "FC media",
    resultHrMax: "FC máx",
    resultPace: "Ritmo",
    resultEffort: "Esfuerzo",
    resultFeeling: "Sensaciones",

    // Skip modal — Sprint 12 S12.7: modal mono-opción tras la
    // consolidación v9.2 §1. La opción "logística" se eliminó (usar
    // Posponer/Mover en su lugar).
    skipModalTitle: "¿Saltar esta sesión por dolor o lesión?",
    skipModalPhysical: "Física (fatiga, molestia, dolor)",
    skipModalPhysicalHint:
      "La próxima sesión intensa se suaviza a Z2 para que recuperes.",
    skipModalConfirm: "Confirmar",
    skipModalCancel: "Cancelar",

    // Delete plan modal
    deleteModalTitle: "¿Eliminar el plan activo?",
    deleteModalBody:
      "El histórico se mantiene pero perderás el seguimiento de las próximas sesiones. Puedes crear uno nuevo cuando quieras.",
    deleteModalConfirm: "Eliminar",

    // Toasts
    toastSessionCompleted: "Sesión marcada como completada.",
    toastSessionSkipped: "Sesión saltada.",
    toastSkipPhysicalAdapted:
      "Sesión saltada. Hemos suavizado la próxima sesión intensa a Z2.",
    toastSkipAlertConsecutive:
      "Has saltado muchas sesiones. Considera reducir el volumen un 15-20%.",
    toastPlanCreated: "Plan creado. ¡Vamos!",
    toastPlanDeleted: "Plan eliminado.",
    toastError: "Algo no ha ido bien. Vuelve a intentarlo.",
    toastProfileIncomplete:
      "Necesitas completar tu perfil primero. Te llevamos al onboarding.",

    // Sesión 33 EXTENDIDA — Alba feature flexible scheduling.
    // Header alternativo del SessionCard cuando flexible_scheduling=true.
    // `{n}` se sustituye en runtime por el índice 1-based de sesiones
    // útiles dentro de la semana (rest no cuenta).
    sessionCardFlexibleLabel: "Sesión {n}",

    // Recommendations card (texto principal viene del backend)
    recommendationsTitle: "Recomendaciones",

    // Create plan form
    createRaceDistanceLabel: "Distancia objetivo",
    createRaceDateLabel: "Fecha de la carrera",
    createSubmit: "Crear plan",
    raceDistance5k: "5K",
    raceDistance10k: "10K",
    raceDistance15k: "15K",
    raceDistance20k: "20K",
    raceDistanceHalfMarathon: "Media maratón (21K)",
    raceDistanceMarathon: "Maratón (42K)",
    raceDistanceTrailShort: "Trail corto",
    raceDistanceTrailLong: "Trail largo",
    raceDistancePersonalChallenge: "Reto personal (sin carrera)",

    // Sprint 13 S13.6a — Modo de plan (carrera vs progresión, v10 §2).
    createModeLabel: "¿Tienes una carrera objetivo?",
    // F12 (pre-flight): ayuda SIEMPRE VISIBLE bajo la pregunta — un novato no
    // sabe qué implica cada opción. Se muestra antes de elegir, no en tooltip.
    createModeHelp:
      "Elige “Sí” si te preparas para una carrera con fecha concreta (10K, media maratón…). Elige “No” si solo quieres empezar a correr y mejorar de forma general.",
    createModeRace: "Sí, tengo una carrera",
    createModeProgression: "No, quiero seguir entrenando y mejorar",
    createProgressionHint:
      "Plan de Progresión de 12 semanas, sin fecha de carrera. Al terminar podrás apuntarte a una carrera o seguir progresando.",

    // Validación cliente
    errorRaceDateRequired: "Selecciona la fecha de tu carrera.",
    errorRaceDateInPast: "La fecha tiene que ser futura.",
    errorRaceDateTooFar: "Como mucho 1 año por delante.",
    errorRaceDistanceRequired: "Selecciona la distancia.",
    // v15.5-§8.4: defensa frontend del error backend `beginner_max_5k`.
    // El filtro del dropdown ya debería evitar el caso; este mensaje
    // aparece solo si hay desincronización (level cambió entre fetch y
    // submit, o cliente manipulado).
    errorBeginnerMax5k:
      "Como principiante solo puedes elegir carreras de hasta 5K. Si quieres distancias mayores, cambia tu nivel a Amateur en tu perfil.",

    // Sprint 13 S13.6a — Badges de tipo de plan (v10 §1/§2).
    planTypeBadgeRace: "Carrera objetivo",
    planTypeBadgeProgression: "Plan de Progresión",
    planTypeBadgeRecovery: "Semana de recuperación",

    // Sprint 13 S13.10a — Elige distancia para nueva progresión (v10.1 §2).
    progressionChoiceTitle: "¿Para qué distancia quieres progresar?",
    progressionChoiceSubtitle:
      "Tu Plan de Progresión ha terminado. Elige la distancia objetivo de tu próximo ciclo de 12 semanas.",

    // v16.5.X T1 4b sub-paquete X (Alberto 19/06/26) — PostRaceImmediateBanner.
    // Banner inmediato post-race (race+1) sobre la recovery week activa.
    // Dismissable con re-aparición 24h + expiración 30d en backend.
    // Distinto de postRecovery* (race+8 días, recovery completada).
    // Sub-paquete Y v16.5.AB (Y.C.1): el botón "Mantenimiento" ya genera
    // el plan en vivo (sin badge "Próximamente"). Los toasts viven en
    // `maintenance.*` abajo (3 variantes: first / continuation / reset).
    postRaceImmediateTitle: "¡Felicidades por tu carrera!",
    postRaceImmediateDescription: "¿Qué quieres hacer ahora?",
    postRaceImmediateEmpezarPlan: "Empezar plan nuevo",
    postRaceImmediateMantenimiento: "Mantenimiento",
    postRaceImmediateDismissAriaLabel: "Cerrar mensaje",

    // Sub-paquete Y v16.5.AB (Alberto 19/06/26) — Mantenimiento.
    // Copy de los 3 estados de la response del endpoint Y.B.4:
    //   - `maintenanceToastFirst`: cycle_number=0 + !was_reset (1er
    //     maintenance ever del atleta).
    //   - `maintenanceToastContinuation`: cycle_number>0 + !was_reset
    //     (renovación dentro de los 30d). Interpolación `{cycle}` =
    //     cycle_number+1 (1-based al usuario, 2..6).
    //   - `maintenanceToastReset`: was_reset=true (counter reseteado por
    //     NULL o >30d sin renovar).
    //   - `maintenanceToastError`: cualquier fallo de red / backend.
    // FLAT keys (no nested object) para mantener compatibilidad con el
    // pattern del resto de `t.plan.*` — algunos consumidores indexan vía
    // `t.plan[key]` y esperan que todos los values sean strings.
    maintenanceToastFirst: "Plan de mantenimiento creado.",
    maintenanceToastContinuation: "Ciclo {cycle} de 6 iniciado.",
    maintenanceToastReset:
      "Empezando ciclo 1 (tras pausa de más de 30 días).",
    maintenanceToastError:
      "No se pudo crear el plan de mantenimiento. Inténtalo de nuevo.",

    // Sprint 13 S13.10b — Pantalla post-recovery (2 opciones, v10 §1).
    postRecoveryTitle: "¿Cuál es tu próximo objetivo?",
    postRecoverySubtitle:
      "Has completado tu semana de recuperación. Elige cómo quieres seguir.",
    postRecoveryRaceTitle: "Preparar una carrera",
    postRecoveryRaceDesc:
      "Tengo una carrera con fecha. Crea un plan hacia ella.",
    postRecoveryProgressionTitle: "Seguir progresando",
    postRecoveryProgressionDesc:
      "Sin carrera por ahora. Otro ciclo de 12 semanas para mejorar.",

    // Sprint 13 S13.10b — Añadir carrera desde Plan de Progresión (v10.1 §3).
    addRaceButton: "Añadir una carrera",

    // v16.3 §1.1 B.3 — RaceDayCard en PlanPage.
    raceDayCardTitle: "Día de carrera",
    raceDayCardUpcoming: "Faltan {days} días para tu carrera.",
    raceDayCardToday: "¡Hoy es tu día! Disfrútalo.",
    raceDayCardPast: "Tu carrera fue el {date}.",
    raceDayCardCta: "Reportar resultado",
    raceDayCardCompletedNote:
      "Resultado registrado. Puedes editarlo desde la página de carreras.",
    raceDayCardResultSavedToast:
      "Resultado guardado. ¡Buena carrera!",

    // v16.3 §G.2 + v16.4 §1 — RaceWeekSection (gap-fill backend §G.1).
    // Renderizada en PlanPage entre WeekNavigator/WeekView y RaceDayCard
    // SOLO cuando race_date no cae en lunes (gap >= 1, race_week presente
    // en plan.weeks con phase="race_week"). Caso gap=0 (race=lunes) no
    // muestra esta sección porque la activación vive dentro del taper.
    raceWeekSectionTitle: "Últimos días antes de tu carrera",
    raceWeekSectionSubtitle:
      "Descanso suave y activación previa al día de carrera.",
    addRaceTitle: "Añadir una carrera",
    addRaceSurfaceLabel: "Superficie",
    addRaceSurfaceAsphalt: "Asfalto",
    addRaceSurfaceTrail: "Trail",
    addRaceSubmit: "Reorientar el plan",
    // Diálogo de confirmación de modalidad trail (409). Copy CLAVE: deja
    // claro que pasa a MIXTO (base asfalto + tirada larga en monte), NO
    // que todo se convierte en trail.
    trailModalityTitle: "¿Añadir trail como modalidad?",
    trailModalityBody:
      "Tu perfil es de asfalto. Para preparar una carrera de trail, tu entrenamiento pasará a modalidad mixta: mantienes tu base de asfalto y se añade una tirada larga en monte cada cierto tiempo. No se convierte todo en trail.",
    trailModalityConfirm: "Sí, añadir trail",
    trailModalityCancel: "Cancelar",

    // Sprint 11 S11.1 — Fecha de inicio del plan (motor v8 §1).
    // El atleta elige cuándo arrancar (today..today+30d). Si tiene
    // reloj y es amateur, ese día tocará el TEST-VAM (S9.5).
    startDateStepTitle: "¿Cuándo quieres empezar?",
    startDateStepDescription:
      "Tu plan empezará el día que elijas. Si tienes reloj y eres amateur, ese día harás el Test VAM para calibrar tus ritmos.",
    startDateStepLabel: "Fecha de inicio",
    startDateStepHelperToday: "Hoy (recomendado si quieres empezar ya)",
    startDateStepHelperNextMonday: "Próximo lunes",
    startDateStepHelperOther: "Otra fecha",
    startDateStepErrorBeforeToday:
      "La fecha de inicio no puede ser anterior a hoy.",
    startDateStepErrorAfter30Days:
      "La fecha de inicio no puede ser más de 30 días después de hoy.",
    startDateStepErrorAfterRaceDate:
      "La fecha de inicio debe ser anterior a la fecha de carrera.",

    // Sprint 11 S11.2 — Gate "plan oculto hasta TEST-VAM completado"
    // (motor v8 §1 "Opción B" visual). El atleta amateur+watch con
    // test_vam planned ve SOLO esta vista hasta calibrar la VAM.
    preVamGateTitle: "Tu primer entrenamiento",
    // Pre-flight F16: ya hay algo que hacer YA (el rodaje suave). El plan
    // completo con ritmos/zonas se desbloquea al completar el Test VAM.
    preVamGateDescription:
      "Empieza con este rodaje suave para soltar las piernas. Cuando completes el Test VAM, desbloquearás tu plan completo con tus entrenamientos personalizados.",

    // Sprint 11 S11.3 — Test VAM Mensaje al usuario (motor v8 §2).
    // Pantalla presentación + 6 pasos + consejo + nomenclatura
    // obligatoria. Reemplaza el render del structure_json para
    // session_type='test_vam' (no coexisten).
    testVamHeroTitle: "Tu primer entrenamiento: el Test de 5 minutos",
    testVamHeroDescription:
      "Antes de crear tu plan personalizado necesitamos saber cómo estás hoy. Para eso haremos un test muy sencillo: correr 5 minutos al máximo esfuerzo que puedas sostener.",
    testVamHeroSubDescription:
      "Con el resultado calculamos tus ritmos de entrenamiento y tus zonas de frecuencia cardíaca. A partir de ahí, cada sesión de tu plan estará ajustada exactamente a ti.",
    testVamStepsTitle: "Cómo hacerlo",
    testVamStep1Title: "Encuentra un tramo adecuado",
    testVamStep1Body:
      "Busca un tramo llano, lo más recto posible y de al menos 1 km de longitud — una pista de atletismo es ideal, aunque también vale cualquier calle o camino recto sin curvas pronunciadas. Evita cuestas, cruces y zonas con mucho tráfico.",
    testVamStep2Title: "Calentamiento",
    testVamStep2Body:
      "Calienta 10 minutos trotando suave + 4 acelerones cortos de 60 m.",
    testVamStep3Title: "El test",
    testVamStep3Body:
      "Una vez completado el calentamiento, prepara tu reloj e inicia la actividad de carrera antes de arrancar. Cuando estés listo, corre durante 5 minutos al máximo ritmo que puedas mantener de principio a fin. No es un sprint — arranca fuerte pero controlado, con un esfuerzo que puedas sostener los 5 minutos completos sin colapsar. Si en el minuto 2 ya no puedes más, has salido demasiado rápido.",
    testVamStep4Title: "Guarda la actividad",
    testVamStep4Body: "Al terminar, guarda la actividad en tu reloj.",
    testVamStep5Title: "Nomenclatura obligatoria",
    testVamStep5BodyTemplate:
      "Nombra la actividad exactamente así: TEST VAM ({date}). Esto permite que Nexum identifique el test correctamente.",
    testVamStep6Title: "Reportar resultado",
    testVamStep6Body:
      "Si tienes Strava vinculado, los datos se importarán automáticamente. Si no, introdúcelos a mano: distancia recorrida, FC media y FC máxima.",
    testVamTipTitle: "Consejo",
    testVamTipBody:
      "El error más común es salir demasiado rápido. Empieza fuerte pero controlado — si te quedas sin piernas a los 2 minutos, el test no será válido.",

    // Sprint 11 S11.5 — Reprogramación lazy de TEST-VAM missed
    // (motor v8 §3 "no completó el test → reprogramar 4 días después").
    // Banner one-shot que aparece cuando el backend acaba de
    // reprogramar el test al detectar que el atleta no lo completó.
    vamRescheduleBannerTitle: "Test VAM reprogramado",
    vamRescheduleBannerBody:
      "Hemos reprogramado tu Test VAM para el {date}. Cuando estés listo, hazlo y tu plan se ajustará automáticamente.",
    vamRescheduleBannerDismiss: "Entendido",

    // v16.3 §4 — etiqueta INFORMATIVA de superficie recomendada por sesión.
    // Renderizada en SessionDetailPage (no en SessionCard) como sección
    // dedicada. El reparto mod-4 caco_plus en plan "Ambos" se decide en
    // backend según week_number (§4.2 nota 2).
    surfaceRecommendedLabel: "Superficie recomendada",
    surfaceLabelAsfalto: "Asfalto",
    surfaceLabelMontana: "Montaña",
    surfaceLabelPistaAsfalto: "Pista o asfalto",
    surfaceLabelPistaLlano: "Pista o llano",
    surfaceLabelFree: "A elección",

    // TEST-VAM (Sprint 6 Bloque 5)
    testBadgeLabel: "TEST",
    // v16.1 §8 sub-bloque 3: sufijos para distinguir KIND del TEST-VAM
    // junto al nombre de la sesión. El espacio antes del paréntesis se
    // añade EN CÓDIGO (helper labelForVamTestKind) por convención —
    // espacios al inicio de cadenas i18n son traps silenciosos.
    vamTestKindInitialLabel: "(inicial)",
    vamTestKindPeriodicLabel: "(periódico)",
    vamTestKindFinalLabel: "(post-carrera)",
    testVamProtocolTitle: "Protocolo del test",
    testVamReportButton: "Reportar resultado",
    testVamModalTitle: "Resultado del TEST-VAM",
    testVamModalIntro:
      "Reporta los datos del esfuerzo de 5 minutos para recalibrar tus ritmos.",
    testVamDistanceLabel: "Distancia recorrida (metros)",
    testVamDistancePlaceholder: "Ej: 1500",
    // v11 §1: FC media y FC máx ahora OBLIGATORIAS (sin "(opcional)").
    testVamHrAvgLabel: "FC media",
    testVamHrAvgPlaceholder: "Ej: 165",
    testVamHrMaxLabel: "FC máxima",
    testVamHrMaxPlaceholder: "Ej: 188",
    // v11 §1: ritmo medio OPCIONAL, formato mm:ss.
    testVamPaceLabel: "Ritmo medio /km (opcional)",
    testVamPacePlaceholder: "Ej: 5:30",
    testVamPaceHelper: "El ritmo medio del esfuerzo, como lo muestra tu reloj.",
    testVamSubmitButton: "Guardar resultado",
    testVamCancelButton: "Cancelar",
    testVamErrorDistance:
      "Introduce una distancia válida en metros (mayor que 0).",
    testVamErrorHrRange: "FC fuera de rango razonable (40-230).",
    // v11 §1: las 3 obligatorias.
    testVamErrorHrRequired: "Introduce la FC media y la FC máxima del test.",
    testVamErrorPace: "Ritmo no válido. Usa el formato mm:ss (ej. 5:30).",
    testVamSuccessToastTemplate:
      "VAM actualizada: {pace} (FC máx {hr}). Plan recalibrado.",
    testVamSuccessToastNoHr: "VAM actualizada: {pace}. Plan recalibrado.",
    // v16.1 §8 sub-bloque D + v16.2 §7: tras completar el TEST-VAM
    // FINAL de la recovery week (vam_test_kind='final'), mensaje
    // motivacional para invitar al atleta a iniciar nueva planificación.
    // Reemplaza el template estándar al renderizar el toast post-submit.
    testVamSuccessToastFinal:
      "¡Lo has conseguido! ¿Listo para seguir mejorando? Inicia tu nueva planificación.",

    // Sprint 10 S10.1b — Posponer sesión (v7.1 §8).
    // El atleta puede mover una sesión 1 día y la cascada arrastra las
    // siguientes para mantener el espacio. Backend rechaza test_vam y
    // resuelve el overflow vs race_date con auto-skips de menor impacto.
    postponeButton: "Posponer",
    postponeButtonTooltip:
      "Mueve esta sesión 1 día y desplaza las siguientes.",
    postponeButtonDisabledTestVam: "El TEST-VAM no se puede posponer.",
    postponeModalTitle: "Posponer esta sesión",
    postponeModalBody:
      "Esta sesión se moverá 1 día. Todas las sesiones siguientes se desplazarán también para mantener el espacio entre ellas.",
    postponeModalRaceWarning:
      "⚠️ Las modificaciones de los entrenamientos pueden poner en peligro tus objetivos de competición.",
    postponeModalCancelButton: "Cancelar",
    postponeModalConfirmButton: "Posponer 1 día",
    postponeSuccessSimple:
      "Sesión pospuesta. {sessions_shifted} sesiones desplazadas 1 día.",
    postponeSuccessWithSkips:
      "Sesión pospuesta. {sessions_shifted} sesiones desplazadas. Se omitió {sessions_skipped_auto} sesión(es) del taper para mantener tu fecha de carrera.",
    postponeErrorTestVam:
      "El TEST-VAM no se puede posponer. Es la sesión de calibración fundamental para tu plan.",
    postponeErrorCompleted:
      "No puedes posponer una sesión ya completada o saltada.",
    postponeErrorRaceOverflow:
      "No es posible posponer más manteniendo tu fecha de carrera ({race_date}). Considera reprogramar tu carrera si necesitas este desplazamiento.",
    postponeErrorForbidden: "No tienes permisos sobre esta sesión.",
    postponeErrorGeneric:
      "Ocurrió un error al posponer la sesión. Inténtalo de nuevo.",

    // Sprint 10 S10.2b — Reportar fatiga (v7.1 §9).
    // El atleta sustituye la sesión planificada por un rodaje Z2 suave
    // de 30 min en el MISMO día. Backend solo acepta hoy o mañana
    // (window=today+1); cualquier otra fecha → use Posponer.
    fatigueButton: "Reportar fatiga",
    fatigueButtonTooltipDisabledTestVam: "El TEST-VAM no se puede sustituir.",
    fatigueButtonTooltipDisabledRest: "No reportar fatiga sobre descanso.",
    fatigueButtonTooltipDisabledPast: "Solo sobre sesión de hoy.",
    fatigueButtonTooltipDisabledFarFuture:
      "Solo sobre sesión de hoy o mañana.",
    fatigueModalTitle: "¿Reportar fatiga hoy?",
    fatigueModalBody:
      "Tu sesión de hoy se sustituirá por un rodaje Z2 muy suave de 30 minutos.",
    fatigueModalWarning:
      "La sesión planificada se pierde sin compensación. Tu plan no se desplaza.",
    fatigueModalSubCopyWithWatch: "Trota en zona conversacional (Z2).",
    fatigueModalSubCopyNoWatch:
      "Trota a un ritmo en el que puedas hablar cómodamente.",
    fatigueModalCancelButton: "Cancelar",
    fatigueModalConfirmButton: "Reportar fatiga",
    fatigueSuccessToast:
      "Sesión sustituida por rodaje Z2 30 min. Mantén la intensidad muy baja, es un descanso activo.",
    fatigueErrorTestVam:
      "El TEST-VAM no se puede sustituir por fatiga. Si no puedes hacerlo hoy, pospón la sesión.",
    fatigueErrorRest:
      "No tiene sentido reportar fatiga sobre un día de descanso.",
    fatigueErrorPast: "Solo puedes reportar fatiga sobre tu sesión de hoy.",
    fatigueErrorFarFuture:
      "Solo puedes reportar fatiga sobre tu sesión de hoy o mañana.",
    fatigueErrorNotPlanned:
      "Esta sesión ya está completada o saltada. No se puede reportar fatiga.",
    fatigueErrorAlreadyReported:
      "Ya reportaste fatiga sobre esta sesión. La sustituta está en tu plan.",
    fatigueErrorGeneric:
      "Ocurrió un error al reportar la fatiga. Inténtalo de nuevo.",

    // Badges S10.2b para SessionCard cuando hay reporte de fatiga.
    // - skipped+skip_reason='fatigue' → "Saltada por fatiga" (rojo).
    // - sub (planned mismo session_date) → "Sustituta por fatiga" (warning).
    sessionBadgeSkippedByFatigue: "Saltada por fatiga",
    sessionBadgeFatigueSubstitute: "Sustituta por fatiga",
    // v15.5-§8.3.C: entry point al listado de race_targets.
    racesLinkLabel: "Mis carreras",
  },
  // v15.5-§8.3: sección de gestión de carreras (hasta 5 por user).
  // §8.3.C — lectura. §8.3.D — Add/Edit/Delete. §8.3.E — promote (futuro).
  races: {
    // — Página
    pageTagline: "carreras",
    pageTitle: "Mis carreras",
    subtitle: "Hasta 5 carreras. La principal orienta tu plan.",
    loading: "Cargando carreras…",
    // §8.3.D: empty state evolucionado con CTA inline.
    emptyText: "Empieza añadiendo tu primera carrera.",
    primaryBadge: "Principal",
    errorLoad: "Error al cargar tus carreras. Vuelve a intentarlo.",
    backToPlan: "← Volver al plan",

    // — §8.3.D: botón header + cap warning
    addCta: "+ Añadir carrera",
    capReached: "Máximo 5 carreras. Borra alguna antes de añadir otra.",

    // — §8.3.D: acciones en RaceCard
    cardEditAction: "Editar",
    cardDeleteAction: "Borrar",
    // §8.3.E (futuro): acción promote en RaceCard cuando NO es primary.
    cardPromoteAction: "Marcar como principal",
    // v16.3 B.4: acción "Añadir resultado" (solo si race pasada + no completed)
    cardAddResultAction: "Añadir resultado",

    // — §8.3.D: modal Add/Edit (RaceTargetModal)
    addModalTitle: "Añadir carrera",
    editModalTitle: "Editar carrera",
    fieldNameLabel: "Nombre (opcional)",
    fieldNamePlaceholder: "Ej: Maratón de Sevilla 2026",
    fieldDateLabel: "Fecha",
    fieldDistanceLabel: "Distancia",
    fieldSurfaceLabel: "Superficie",
    modalCancel: "Cancelar",
    modalSubmitCreate: "Añadir",
    modalSubmitEdit: "Guardar",

    // — §8.3.D: errores de form (validación cliente + API)
    errorDateRequired: "Selecciona la fecha de la carrera.",
    errorDateInPast: "La fecha tiene que ser futura.",
    errorDistanceRequired: "Selecciona la distancia.",
    errorInvalidDistance: "Distancia no válida.",
    errorSurfaceRequired: "Selecciona la superficie.",
    errorInvalidSurface: "Superficie no válida.",
    errorMaxReached: "Ya tienes 5 carreras. Borra alguna antes de añadir otra.",
    errorNotFoundOrOwned: "Esta carrera ya no existe o no es tuya.",
    errorGeneric: "Algo ha fallado. Vuelve a intentarlo.",

    // — §8.3.D: modal Delete (DeleteRaceModal)
    deleteModalTitle: "¿Borrar carrera?",
    deleteModalBody: "Vas a borrar \"{race}\". Esta acción no se puede deshacer.",
    deleteModalConfirm: "Borrar",
    // Mensajes UX cuando backend bloquea por política §8.3.B (asumimos
    // §8.3.E ya desplegado: ambos viajan juntos al deploy).
    errorDeletePrimaryWithOthers:
      "Para borrar tu carrera principal, marca otra como principal primero.",
    errorDeleteOnlyPrimary:
      "No puedes borrar tu única carrera. Crea otra y márcala como principal antes.",

    // — §8.3.E: modal Promote (PromoteRaceModal)
    promoteModalTitle: "¿Marcar como principal?",
    // {previous}/{next} = name de la race o label localizado de distance.
    promoteModalBodyWithPrevious:
      "Vas a cambiar tu carrera principal de \"{previous}\" a \"{next}\".",
    promoteModalBodyWithoutPrevious:
      "Vas a marcar \"{next}\" como tu carrera principal.",
    promoteModalRegenerateWarning:
      "Esto regenerará tu plan de entrenamiento. La planificación actual se sustituirá.",
    promoteModalCancel: "Cancelar",
    promoteModalConfirm: "Confirmar y regenerar plan",
    promoteModalConfirmLoading: "Regenerando plan…",

    // — §8.3.E: errores del flujo promote (modal)
    // Copy de plan_regeneration_failed afirma "no se ha cambiado" — válido
    // SOLO porque backend hace rollback completo (verificado empíricamente
    // en test_v15_5_8_3_e_plan_regeneration_failure_rollbacks_swap).
    errorPromotePlanRegen:
      "No hemos podido regenerar tu plan con esta carrera. Tu carrera principal no se ha cambiado. Revisa la fecha y vuelve a intentarlo.",
    errorPromoteNotFound:
      "Esta carrera ya no existe. Vamos a refrescar la lista.",
    errorPromoteProfileNotFound:
      "Necesitas completar tu perfil antes de regenerar el plan.",

    // — §8.3.E: feedback post-promote en RacesPage (toasts + banner)
    promoteToastSuccessWithRegen:
      "Carrera marcada como principal. Plan regenerado.",
    promoteToastSuccessNoPlan:
      "Carrera marcada como principal. Crea un plan para empezar.",
    promoteToastAlreadyPrimary:
      "Esta carrera ya era la principal.",
    promoteBannerText:
      "Tu plan se ha regenerado con la nueva carrera principal.",
    promoteBannerCta: "Ver plan nuevo →",
    promoteBannerDismiss: "Cerrar",

    // — v16.3 B.2 — RaceResultModal (formulario opcional día de carrera)
    resultModalTitle: "Tu resultado",
    resultModalSubtitle:
      "Todos los campos son opcionales. Comparte solo lo que quieras.",
    resultTimeLabel: "Tiempo final",
    resultTimePlaceholder: "h:mm:ss (ej. 3:50:00)",
    resultTimeHint: "Formato libre — usa h:mm:ss o mm:ss.",
    resultDistanceLabel: "Distancia (km)",
    resultDistancePlaceholder: "42.2",
    resultAvgHrLabel: "FC media (bpm)",
    resultMaxHrLabel: "FC máxima (bpm)",
    resultFeelingLabel: "Sensaciones",
    resultFeelingHint: "1 = mal, 5 = excelente.",
    resultWeatherLabel: "Clima",
    resultWeatherCalor: "Calor",
    resultWeatherFrio: "Frío",
    resultWeatherViento: "Viento",
    resultWeatherLluvia: "Lluvia",
    resultWeatherNormal: "Normal",
    resultSubmitCta: "Guardar resultado",
    resultCloseCta: "Cerrar sin guardar",
    resultToastSuccess: "Resultado guardado. ¡Enhorabuena por tu carrera!",

    // v16.3.1 R.1 + v16.4.1 §T4 — pestaña "Resultados" en RacesPage.
    // Reuso de keys existentes para los campos: resultTimeLabel, resultDistanceLabel,
    // resultAvgHrLabel, resultMaxHrLabel, resultFeelingLabel, resultWeather*,
    // cardAddResultAction (botón pendientes).
    tabsLabelRaces: "Mis carreras",
    tabsLabelResults: "Resultados",
    resultsPendingSectionTitle: "Pendientes de reportar",
    resultsRecordedSectionTitle: "Resultados guardados",
    resultsEmptyPending: "No tienes carreras pendientes de reportar.",
    resultsEmptyRecorded: "Aún no has guardado resultados.",
    resultsEmptyAll: "Aún no tienes ninguna carrera.",
    resultErrorTimeInvalid: "Formato de tiempo inválido. Usa h:mm:ss o mm:ss.",
    resultErrorDistanceInvalid:
      "Distancia inválida. Introduce un número positivo en km.",
    resultErrorHrOutOfRange: "FC fuera de rango (40-250 ppm).",
    resultErrorFeelingInvalid: "Sensaciones inválidas (debe ser 1-5).",
    resultErrorGeneric:
      "No hemos podido guardar el resultado. Inténtalo de nuevo.",
  },
  strava: {
    sectionTitle: "Strava",
    // Estado: no conectado.
    notConnectedHelp: "Conecta tu cuenta para que Nexum lea tus carreras.",
    connectCta: "Conectar con Strava",
    // Estado: conectado.
    // Plural manejado en el componente con un helper local.
    connectedActivitiesOne: "Strava conectado · 1 actividad importada",
    connectedActivitiesMany: "Strava conectado · {count} actividades importadas",
    connectedNoActivities: "Strava conectado · sin actividades aún",
    lastSyncedAt: "Última sincronización: {when}",
    syncCta: "Sincronizar ahora",
    syncing: "Sincronizando…",
    disconnectCta: "Desconectar",
    // Estado: desconectado pero con actividades en histórico.
    disconnectedWithHistory:
      "Desconectado · {count} actividades en tu histórico",
    // Toast post-callback.
    connectedToastOne: "Conexión OK. Hemos importado 1 actividad.",
    connectedToastMany: "Conexión OK. Hemos importado {count} actividades.",
    connectedToastNone:
      "Conexión OK. No encontramos carreras nuevas en los últimos 90 días.",
    syncToastNone: "Sin actividades nuevas que sincronizar.",
    syncToastOne: "1 actividad nueva.",
    syncToastMany: "{count} actividades nuevas.",
    disconnectedToast: "Strava desconectado. Tu histórico se mantiene.",
    // Estados del polling de import (Sprint C Bloque 2)
    importingZero: "Importando actividades…",
    importingOne: "Importando actividades… (1 importada)",
    importingMany: "Importando actividades… ({count} importadas)",
    importTimeoutWarning:
      "La importación está tardando más de lo esperado. Te avisamos cuando termine — puedes seguir usando Nexum mientras tanto.",
    importToastZero:
      "Importación completada. No encontramos carreras en los últimos 90 días.",
    importToastOne: "Importación completada. 1 actividad importada.",
    importToastMany:
      "Importación completada. {count} actividades importadas.",
    importFailedToast:
      "La importación ha fallado. Intenta sincronizar manualmente.",
    // Errores Strava.
    errorGeneric: "No hemos podido hablar con Strava. Vuelve a intentarlo.",
    errorReauth:
      "Tu sesión de Strava ha caducado. Vuelve a conectar para sincronizar.",
    errorAccessDenied: "No autorizaste a Nexum a leer tus carreras.",
  },
  notFound: {
    title: "No encontramos esta página.",
    cta: "Volver al inicio",
  },
} as const;

/**
 * Traduce un código de error del backend a copy de marca.
 * Si no encontramos traducción, devolvemos un mensaje genérico
 * (NO el código crudo — eso filtra info de implementación al usuario).
 */
export function translateError(code: string | undefined | null): string {
  if (!code) return t.auth.errors.unknown_error;
  const errors = t.auth.errors as Record<string, string>;
  return errors[code] ?? t.auth.errors.unknown_error;
}
